import { components } from '../types/backend-types';
import {
    FavoriteRecipe,
    FavoriteRecipeSummary,
    FavoriteRecipeDetail,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Type Aliases for readability

export type InventoryItem = components['schemas']['InventoryItem'];
export type MealPlan = components['schemas']['MealPlanDetail'];
export type SavedMealPlan = components['schemas']['SavedMealPlanDetail'];
// FavoriteRecipe removed from here to avoid conflict
export type ProfileApiResponse = components['schemas']['ProfileResponse'];
export type AppContextResponse = components['schemas']['AppContextResponse'];
export type WineItem = components['schemas']['WineItem'];

// V11-001: Stable Device Identity for Echo Cancellation
const getDeviceID = (): string => {
    let id = localStorage.getItem('v11_stable_device_id');
    if (!id) {
        // Use crypto-safe UUID if available, otherwise fallback to Date/Random
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            id = crypto.randomUUID();
        } else {
            id = Date.now().toString(36) + Math.random().toString(36).substring(2);
        }
        localStorage.setItem('v11_stable_device_id', id);
    }
    return id;
};

const getHeaders = (token?: string, contentType = 'application/json') => {
    const headers: Record<string, string> = {
        'X-Device-ID': getDeviceID()
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    if (contentType && contentType !== 'multipart/form-data') {
        headers['Content-Type'] = contentType;
    }
    return headers;
};

export const fetchInventory = async (token: string): Promise<InventoryItem[]> => {
    const response = await fetch(`${API_URL}/api/v1/inventory`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch inventory");
    return response.json();
};

export const scanInventory = async (token: string, files: File[]): Promise<any> => {
    const formData = new FormData();
    files.forEach(file => formData.append('photos', file));

    const response = await fetch(`${API_URL}/api/v1/inventory/scan`, {
        method: 'POST',
        headers: getHeaders(token, 'multipart/form-data'),
        body: formData
    });

    if (response.status === 413) {
        throw new Error("Photo is too large. Please use a smaller image.");
    }
    if (response.status === 504) {
        throw new Error("Scan timed out (network slow). Try again.");
    }
    if (response.status === 402) {
        const errorData = await response.json();
        const error: any = new Error(errorData.detail || "Monthly scan limit reached");
        error.status = 402;
        throw error;
    }
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Inventory scan failed: ${response.status} ${errText.substring(0, 50)}`);
    }
    return response.json();
};

export const streamMealPlan = async (
    userToken: string,
    directive: string,
    enabledMealTypes: string[],
    onChunk: (data: any) => void,
    options: any = {}
): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/generate-plan`, {
        method: 'POST',
        headers: getHeaders(userToken),
        body: JSON.stringify({
            directive,
            enabled_meal_types: enabledMealTypes,
            diet_philosophy: options.dietPhilosophy,
            macro_profile: options.macroProfile,
            household_size: options.householdSize, // V13
            cached_context: options.cachedContext // TTFT Optimization
        }),
        signal: options.signal
    });

    if (response.status === 402) {
        const errorData = await response.json();
        const error: any = new Error(errorData.detail || "Monthly plan limit reached");
        error.status = 402;
        throw error;
    }

    if (!response.ok) {
        throw new Error(`API Error: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("ReadableStream not supported");

    const decoder = new TextDecoder();
    let buffer = "";

    // Progressive state building
    let planState: any = { id: null, title: null, chef_message: null, status_message: null, meals: [], shopping_list: [], status: null };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete NDJSON lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
            if (!line.trim()) continue;
            try {
                const event = JSON.parse(line);
                switch (event.type) {
                    case 'metadata':
                        planState.id = event.id;
                        planState.title = event.title;
                        planState.chef_message = event.chef_message;
                        break;
                    case 'status':
                        planState.status_message = event.chef_message;
                        if (!planState.chef_message) {
                            planState.chef_message = event.chef_message;
                        }
                        break;
                    case 'meal':
                        // Extract meal data (remove 'type' field)
                        const { type, ...mealData } = event;
                        planState.meals.push(mealData);
                        break;
                    case 'done':
                        planState.shopping_list = event.shopping_list;
                        planState.status = event.status;
                        if (event.chef_message) {
                            planState.chef_message = event.chef_message;
                        }
                        break;
                    case 'error':
                        throw new Error(event.message);
                    case 'partial_failure':
                        planState.chef_message = event.message;
                        break;
                }
                onChunk({ ...planState }); // Progressive update
            } catch (e) {
                console.error('NDJSON parse error:', e, 'line:', line);
            }
        }
    }

    return planState;
};

export const addInventoryBatch = async (token: string, items: InventoryItem[]): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/inventory`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify({ items })
    });
    if (!response.ok) throw new Error("Batch update failed");
    return response.json();
};

export const updateInventoryItem = async (token: string, name: string, qty: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/inventory/${encodeURIComponent(name)}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify({ name, qty })
    });
    if (!response.ok) throw new Error(`Failed to update ${name}`);
    return response.json();
};

export const deleteInventoryItem = async (token: string, name: string): Promise<boolean> => {
    const response = await fetch(`${API_URL}/api/v1/inventory/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error(`Failed to delete ${name}`);
    return true;
};

export const fetchHistory = async (token: string, limit = 10, offset = 0): Promise<SavedMealPlan[]> => {
    const response = await fetch(`${API_URL}/api/v1/meal-plans?limit=${limit}&offset=${offset}`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch history");
    return response.json();
};

export const fetchMealPlanDetails = async (token: string, planId: string | number): Promise<SavedMealPlan> => {
    const response = await fetch(`${API_URL}/api/v1/meal-plans/${planId}`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch meal plan details");
    return response.json();
};

export const fetchFavorites = async (token: string): Promise<FavoriteRecipeSummary[]> => {
    const response = await fetch(`${API_URL}/api/v1/favorites`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch favorites");
    return response.json();
};

export const fetchFavoriteDetails = async (token: string, favId: string | number): Promise<FavoriteRecipeDetail> => {
    const response = await fetch(`${API_URL}/api/v1/favorites/${favId}`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch favorite details");
    return response.json();
};

export const saveFavorite = async (token: string, recipe: Partial<FavoriteRecipe>): Promise<FavoriteRecipe> => {
    const response = await fetch(`${API_URL}/api/v1/favorites`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(recipe)
    });
    if (!response.ok) throw new Error("Failed to save favorite");
    return response.json();
};

export const deleteFavorite = async (token: string, favoriteId: string | number): Promise<boolean> => {
    const response = await fetch(`${API_URL}/api/v1/favorites/${favoriteId}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to delete favorite");
    return true;
};

export const deleteHistory = async (token: string, planId: string | number): Promise<boolean> => {
    const response = await fetch(`${API_URL}/api/v1/meal-plans/${planId}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to delete history");
    return true;
};

export const fetchMacros = async (token: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/macros`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch macros");
    return response.json();
};

export const updateMacros = async (token: string, macros: any): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/macros`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(macros)
    });
    if (!response.ok) throw new Error("Failed to update macros");
    return response.json();
};

export const fetchUserProfile = async (token: string): Promise<ProfileApiResponse> => {
    const response = await fetch(`${API_URL}/api/v1/profile`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch profile");
    return response.json();
};

export const updateExclusions = async (token: string, exclusions: string[]): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/exclusions`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify({ exclusions })
    });
    if (!response.ok) throw new Error("Failed to update exclusions");
    return response.json();
};

export const chatRefinePlan = async (userToken: string, chatRequest: any, onChunk: (data: any) => void): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/chat`, {
        method: 'POST',
        headers: getHeaders(userToken),
        body: JSON.stringify({
            ...chatRequest,
            household_size: chatRequest.household_size || (chatRequest as any).householdSize // Handle both naming conventions
        }),
        signal: (chatRequest as any).signal
    });

    if (response.status === 402) {
        const errorData = await response.json();
        const error: any = new Error(errorData.detail || "Regeneration limit reached");
        error.status = 402;
        throw error;
    }

    if (!response.ok) {
        throw new Error(`Chat API Error: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("ReadableStream not supported");
    const decoder = new TextDecoder();
    let buffer = "";

    // State for accumulating chat updates
    let chatState: any = { chef_message: null, meals: [], exclusion_update: null, validation_log: null };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete NDJSON lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
            if (!line.trim()) continue;
            try {
                const event = JSON.parse(line);
                switch (event.type) {
                    case 'progress':
                        onChunk({ chef_message: event.message, meals: chatState.meals });
                        break;
                    case 'update':
                        chatState.chef_message = event.chef_message;
                        chatState.meals = event.meals;
                        chatState.exclusion_update = event.exclusion_update;
                        chatState.validation_log = event.validation_log;
                        onChunk({ ...chatState });
                        break;
                    case 'done':
                        break;
                    case 'error':
                        throw new Error(event.message);
                }
            } catch (e: any) {
                if (e.message && !e.message.includes('Unexpected')) {
                    throw e;
                }
                console.error('NDJSON parse error:', e, 'line:', line);
            }
        }
    }

    return chatState;
};

export const parseInventoryInput = async (token: string, text: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/inventory/parse`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify({ text })
    });
    if (!response.ok) throw new Error("Failed to parse input");
    return response.json();
};

export const updateToggles = async (token: string, toggles: any): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/toggles`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(toggles)
    });
    if (!response.ok) throw new Error("Failed to update toggles");
    return response.json();
};

export const fetchWine = async (token: string): Promise<WineItem[]> => {
    const response = await fetch(`${API_URL}/api/v1/wine`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch wine cellar");
    return response.json();
};

export const addWine = async (token: string, wine: WineItem): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/wine`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify(wine)
    });
    if (!response.ok) throw new Error("Failed to add wine");
    return response.json();
};

export const updateWineItem = async (token: string, itemId: string | number, wine: Partial<WineItem>): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/wine/${itemId}`, {
        method: 'PUT',
        headers: getHeaders(token),
        body: JSON.stringify(wine)
    });
    if (!response.ok) throw new Error("Failed to update wine");
    return response.json();
};

export const deleteWine = async (token: string, itemId: string | number): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/wine/${itemId}`, {
        method: 'DELETE',
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error('Failed to delete wine');
    return response.json();
};

export const updateWine = async (token: string, itemId: string | number, data: Partial<WineItem>): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/wine/${itemId}`, {
        method: 'PATCH',
        headers: getHeaders(token),
        body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update wine');
    return response.json();
};

export const scanWine = async (token: string, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await fetch(`${API_URL}/api/v1/wine/scan`, {
        method: 'POST',
        headers: getHeaders(token, 'multipart/form-data'),
        body: formData
    });

    if (response.status === 413) throw new Error("Photo is too large.");
    if (response.status === 504) throw new Error("Scan timed out.");

    if (!response.ok) throw new Error("Wine label scan failed");
    return response.json();
};

export const parseWineInput = async (token: string, text: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/wine/parse`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify({ text })
    });
    if (!response.ok) throw new Error("Failed to parse wine input");
    return response.json();
};

export const commitMealPlan = async (userToken: string, planId: string | number, overrides: any[] = [], skipWineDeductions: number[] = []): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/meal-plans/${planId}/commit`, {
        method: 'POST',
        headers: getHeaders(userToken),
        body: JSON.stringify({ overrides, skip_wine_ded_ids: skipWineDeductions })
    });
    if (!response.ok) throw new Error("Failed to commit plan");
    return response.json();
};

export const createCheckoutSession = async (token: string, tier: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/payments/stripe/create-checkout-session?tier=${tier}`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to create checkout session");
    return response.json();
};

export const createPortalSession = async (token: string): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/payments/stripe/customer-portal`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to create portal session");
    return response.json();
};

export const fetchAppContext = async (token: string): Promise<AppContextResponse> => {
    const response = await fetch(`${API_URL}/api/v1/app-context`, {
        headers: getHeaders(token)
    });
    if (!response.ok) throw new Error("Failed to fetch app context");
    return response.json();
};

export interface IntentResponse {
    intent: 'GENERATE' | 'REFINE' | 'OFF_TOPIC';
    confidence: number;
    reasoning?: string;
}

export const classifyIntent = async (token: string, command: string): Promise<IntentResponse> => {
    const response = await fetch(`${API_URL}/api/v1/intent`, {
        method: 'POST',
        headers: getHeaders(token),
        body: JSON.stringify({ command })
    });
    if (!response.ok) throw new Error("Intent classification failed");
    return response.json();
};

export const patchMeal = async (
    token: string,
    planId: string,
    day: string,
    mealType: string,
    updates: { wine_selection?: boolean }
): Promise<any> => {
    const response = await fetch(`${API_URL}/api/v1/meal-plans/${planId}/meals`, {
        method: 'PATCH',
        headers: getHeaders(token),
        body: JSON.stringify({
            day,
            meal_type: mealType,
            updates
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: "Unknown error" }));
        throw new Error(err.detail || "Failed to update meal");
    }
    return response.json();
};
